/**
 * /webhooks — Webhooks de saída (ADR-0062).
 *
 * Lista de endpoints + KPIs de entrega, form em SlideOverPanel (checklist
 * de eventos), modal copy-once do secret, log de entregas com "Reenviar"
 * e botão "Enviar teste".
 *
 * Permissões: webhook:view (ler) / webhook:configurar (tudo que muda).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCopy, FiEdit2, FiPlus, FiRefreshCw, FiSend, FiTrash2, FiZap,
} from "react-icons/fi";
import styled from "styled-components";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { PageContainer } from "../../styles/shared";
import { formatarDataLocal } from "../../utils/dinheiro";


import {
  Card, EmptyMsg, ErrorMsg, Input, Pill, SectionTitle, Table,
} from "../Orcamentos/styles";

import {
  SITUACOES_ENTREGA,
  atualizarEndpoint,
  criarEndpoint,
  excluirEndpoint,
  listarEndpoints,
  listarEntregas,
  listarEventosDisponiveis,
  obterResumo,
  reenviarEntrega,
  rotacionarSecret,
  testarEndpoint,
} from "../../services/webhooks";

// Data + hora local em pt-BR (o util do projeto cobre só a data).
function dataHora(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${formatarDataLocal(iso)} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}


// O Table do design system gruda o <th> em top:var(--app-header-height)
// — correto no scroll do body, mas dentro do SlideOverPanel (scroll
// proprio) isso EMPURRA o cabecalho para o meio da tabela. Aqui o th
// fica no fluxo normal.
const TabelaSemSticky = styled(Table)`
  th {
    position: static;
  }
`;

const Grupo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;

const Rotulo = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Ajuda = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
`;

const ListaEventos = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 14px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const EventoCheck = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  cursor: pointer;

  input { accent-color: ${({ theme }) => theme.primary}; }
  code {
    font-size: 11px;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

const SecretBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px dashed ${({ theme }) => theme.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.surfaceAlt || theme.surface};
  font-family: monospace;
  font-size: 13px;
  word-break: break-all;
`;

const AcoesLinha = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const BotaoLinha = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.text};
  font-size: 12px;
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.surfaceAlt || theme.background}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;


function FormEndpoint({ aberto, endpoint, eventos, onFechar, onSalvo }) {
  const { request } = useAuth();
  const editando = Boolean(endpoint?.id);

  const [nome, setNome] = useState("");
  const [url, setUrl] = useState("");
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!aberto) return;
    setNome(endpoint?.nome || "");
    setUrl(endpoint?.url || "");
    setSelecionados(new Set(endpoint?.eventos || []));
    setErro(null);
  }, [aberto, endpoint]);

  function alternar(codigo) {
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (novo.has(codigo)) novo.delete(codigo);
      else novo.add(codigo);
      return novo;
    });
  }

  async function salvar() {
    setErro(null);
    const dados = { nome, url, eventos: Array.from(selecionados) };
    try {
      const resposta = editando
        ? await atualizarEndpoint(request, endpoint.id, dados)
        : await criarEndpoint(request, dados);
      onSalvo(resposta, { criado: !editando });
    } catch (err) {
      const payload = err?.payload || {};
      setErro(
        payload.detalhe || payload.url?.[0] || payload.eventos?.[0]
        || payload.nome?.[0] || err?.message || "Falha ao salvar endpoint.",
      );
      throw err; // AsyncButton mostra o estado de erro
    }
  }

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title={editando ? `Editar ${endpoint?.nome}` : "Novo endpoint de webhook"}
      subtitle="O PharuERP fará POST assinado nesta URL a cada evento assinado."
      footer={(
        <AsyncButton onClick={salvar} silentError successMessage={editando ? "Endpoint atualizado." : "Endpoint criado."}>
          {editando ? "Salvar alterações" : "Criar endpoint"}
        </AsyncButton>
      )}
    >
      {erro && <ErrorMsg>{erro}</ErrorMsg>}
      <Grupo>
        <Rotulo>Nome</Rotulo>
        <Input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Ex.: RD Station, Zapier, site institucional"
        />
      </Grupo>
      <Grupo>
        <Rotulo>URL de destino</Rotulo>
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…"
        />
        <Ajuda>
          Só HTTPS. Endereço interno/privado é recusado. A resposta precisa
          ser 2xx — qualquer outra coisa entra na fila de retry
          (1min → 5min → 15min → 1h → 6h → 24h).
        </Ajuda>
      </Grupo>
      <Grupo>
        <Rotulo>Eventos assinados</Rotulo>
        <ListaEventos>
          {eventos.map((ev) => (
            <EventoCheck key={ev.codigo}>
              <input
                type="checkbox"
                checked={selecionados.has(ev.codigo)}
                onChange={() => alternar(ev.codigo)}
              />
              <span>{ev.rotulo} <code>{ev.codigo}</code></span>
            </EventoCheck>
          ))}
        </ListaEventos>
      </Grupo>
    </SlideOverPanel>
  );
}


function PainelSecret({ secret, contexto, onFechar }) {
  const toast = useToast();
  return (
    <SlideOverPanel
      open={Boolean(secret)}
      onClose={onFechar}
      title="Guarde o secret agora"
      subtitle={contexto}
    >
      <Ajuda style={{ marginBottom: 10 }}>
        Este é o único momento em que o secret aparece — ele valida a
        assinatura <code>X-Pharu-Signature</code> das entregas. Se perder,
        rotacione para gerar outro (o antigo deixa de valer na hora).
      </Ajuda>
      <SecretBox>
        <span>{secret}</span>
        <BotaoLinha
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(secret);
            toast.success("Secret copiado.");
          }}
        >
          <FiCopy /> Copiar
        </BotaoLinha>
      </SecretBox>
    </SlideOverPanel>
  );
}


function PainelEntregas({ endpoint, onFechar }) {
  const { request } = useAuth();
  const toast = useToast();
  const [entregas, setEntregas] = useState([]);
  const [total, setTotal] = useState(0);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    if (!endpoint) return;
    setErro(null);
    try {
      const payload = await listarEntregas(request, endpoint.id, { limite: 50 });
      setEntregas(payload?.entregas || []);
      setTotal(payload?.total || 0);
    } catch (err) {
      setErro(err?.message || "Falha ao carregar entregas.");
    }
  }, [request, endpoint]);

  useEffect(() => { carregar(); }, [carregar]);

  async function reenviar(entrega) {
    try {
      await reenviarEntrega(request, endpoint.id, entrega.id);
      toast.success("Reenvio disparado.");
      carregar();
    } catch (err) {
      toast.error(err?.payload?.detalhe || "Não foi possível reenviar.");
    }
  }

  return (
    <SlideOverPanel
      open={Boolean(endpoint)}
      onClose={onFechar}
      title={endpoint ? `Entregas — ${endpoint.nome}` : ""}
      subtitle={`${total} entrega(s) registradas (últimas 50 exibidas)`}
      width="min(94vw, 780px)"
    >
      {erro && <ErrorMsg>{erro}</ErrorMsg>}
      {!entregas.length && !erro && <EmptyMsg>Nenhuma entrega ainda.</EmptyMsg>}
      {entregas.length > 0 && (
        <TabelaSemSticky>
          <colgroup>
            <col />
            <col style={{ width: "132px" }} />
            <col style={{ width: "58px" }} />
            <col style={{ width: "54px" }} />
            <col style={{ width: "142px" }} />
            <col style={{ width: "112px" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Evento</th>
              <th>Situação</th>
              <th>Tent.</th>
              <th>HTTP</th>
              <th>Quando</th>
              <th aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {entregas.map((e) => {
              const st = SITUACOES_ENTREGA[e.situacao] || { label: e.situacao, tone: "neutral" };
              return (
                <tr key={e.id} title={e.erro_resumo || ""}>
                  <td><code>{e.evento}</code></td>
                  <td><Pill $tone={st.tone}>{st.label}</Pill></td>
                  <td>{e.tentativas}</td>
                  <td>{e.http_status || "—"}</td>
                  <td>{dataHora(e.created_at)}</td>
                  <td>
                    {(e.situacao === "ESGOTADA" || e.situacao === "FALHOU") && (
                      <BotaoLinha
                        type="button"
                        disabled={Boolean(e.payload_limpo_em)}
                        title={e.payload_limpo_em
                          ? "Payload já foi limpo pela retenção de 30 dias."
                          : "Zera as tentativas e tenta agora."}
                        onClick={() => reenviar(e)}
                      >
                        <FiRefreshCw /> Reenviar
                      </BotaoLinha>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TabelaSemSticky>
      )}
    </SlideOverPanel>
  );
}


export default function Webhooks() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const toast = useToast();
  const podeConfigurar = isMaster || hasAllPermissions(["webhook:configurar"]);

  const [endpoints, setEndpoints] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [erro, setErro] = useState(null);

  const [formAberto, setFormAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [secretExibido, setSecretExibido] = useState(null);
  const [secretContexto, setSecretContexto] = useState("");
  const [endpointEntregas, setEndpointEntregas] = useState(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const [lista, kpis] = await Promise.all([
        listarEndpoints(request),
        obterResumo(request),
      ]);
      setEndpoints(Array.isArray(lista) ? lista : (lista?.results || []));
      setResumo(kpis);
    } catch (err) {
      setErro(err?.message || "Falha ao carregar webhooks.");
    }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    listarEventosDisponiveis(request)
      .then((p) => setEventos(p?.eventos || []))
      .catch(() => setEventos([]));
  }, [request]);

  const kpis = useMemo(() => ([
    { label: "Endpoints ativos", value: resumo?.endpoints_ativos ?? "—" },
    { label: "Entregas (24h)", value: resumo?.entregas_24h ?? "—" },
    {
      label: "Entregues (24h)", value: resumo?.entregues_24h ?? "—",
      tone: resumo && resumo.entregas_24h > 0
        && resumo.entregues_24h < resumo.entregas_24h ? "warning" : undefined,
    },
    {
      label: "Aguardando retry", value: resumo?.aguardando_retry ?? "—",
      tone: resumo?.aguardando_retry ? "warning" : undefined,
    },
  ]), [resumo]);

  function aoSalvar(resposta, { criado }) {
    setFormAberto(false);
    setEditando(null);
    if (criado && resposta?.secret) {
      setSecretExibido(resposta.secret);
      setSecretContexto(`Endpoint "${resposta.nome}" criado.`);
    }
    carregar();
  }

  async function testar(endpoint) {
    const entrega = await testarEndpoint(request, endpoint.id);
    if (entrega?.situacao === "ENTREGUE") {
      toast.success(`teste.ping entregue (HTTP ${entrega.http_status}).`);
    } else {
      const motivo = (entrega?.erro_resumo || "sem resposta 2xx").replace(/\.$/, "");
      toast.error(`Teste falhou: ${motivo}.`);
    }
  }

  async function rotacionar(endpoint) {
    const payload = await rotacionarSecret(request, endpoint.id);
    setSecretExibido(payload?.secret || null);
    setSecretContexto(`Secret de "${endpoint.nome}" rotacionado — o anterior parou de valer.`);
  }

  async function excluir(endpoint) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Excluir o endpoint "${endpoint.nome}"? O histórico de entregas some junto.`)) return;
    try {
      await excluirEndpoint(request, endpoint.id);
      toast.success("Endpoint excluído.");
      carregar();
    } catch (err) {
      toast.error(err?.payload?.detalhe || "Não foi possível excluir.");
    }
  }

  return (
    <PageContainer>
      <EntityHeader
        title="Webhooks de saída"
        subtitle="Notifique ferramentas externas quando leads e orçamentos mudarem de estado."
        icone={FiZap}
        primaryAction={podeConfigurar ? {
          label: "Novo endpoint",
          icon: FiPlus,
          onClick: () => { setEditando(null); setFormAberto(true); },
        } : undefined}
      />

      <KpiStrip items={kpis} />

      {erro && <ErrorMsg>{erro}</ErrorMsg>}

      <Card>
        <SectionTitle>Endpoints</SectionTitle>
        {!endpoints.length && (
          <EmptyMsg>
            Nenhum endpoint cadastrado. Crie um e aponte para a URL da sua
            ferramenta de marketing/automação — a documentação completa está
            em pharu.com.br/desenvolvedores.
          </EmptyMsg>
        )}
        {endpoints.length > 0 && (
          <Table>
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "30%" }} />
              <col style={{ width: "90px" }} />
              <col style={{ width: "110px" }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>Nome</th>
                <th>URL</th>
                <th>Eventos</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {endpoints.map((e) => (
                <tr key={e.id}>
                  <td>{e.nome}</td>
                  <td title={e.url}>{e.url}</td>
                  <td title={(e.eventos || []).join(", ")}>
                    {(e.eventos || []).length} evento(s)
                  </td>
                  <td>
                    {e.ativo
                      ? <Pill $tone="success">Ativo</Pill>
                      : (
                        <Pill
                          $tone="danger"
                          title={e.desativado_automaticamente_em
                            ? `Desativado automaticamente em ${dataHora(e.desativado_automaticamente_em)} após falhas seguidas.`
                            : "Desativado manualmente."}
                        >
                          Desativado
                        </Pill>
                      )}
                  </td>
                  <td>
                    <AcoesLinha>
                      <BotaoLinha type="button" onClick={() => setEndpointEntregas(e)}>
                        Entregas
                      </BotaoLinha>
                      {podeConfigurar && (
                        <>
                          <AsyncButton
                            variant="icon"
                            title="Enviar evento de teste (teste.ping)"
                            onClick={() => testar(e)}
                            silentSuccess
                            silentError
                          >
                            <FiSend />
                          </AsyncButton>
                          <BotaoLinha
                            type="button"
                            title="Editar nome, URL, eventos e status"
                            onClick={() => { setEditando(e); setFormAberto(true); }}
                          >
                            <FiEdit2 />
                          </BotaoLinha>
                          <AsyncButton
                            variant="icon"
                            title="Rotacionar secret (o atual para de valer)"
                            onClick={() => rotacionar(e)}
                            silentSuccess
                            errorMessage="Não foi possível rotacionar o secret."
                          >
                            <FiRefreshCw />
                          </AsyncButton>
                          <BotaoLinha type="button" title="Excluir" onClick={() => excluir(e)}>
                            <FiTrash2 />
                          </BotaoLinha>
                          {!e.ativo && (
                            <AsyncButton
                              variant="secondary"
                              successMessage="Endpoint reativado."
                              onClick={async () => {
                                await atualizarEndpoint(request, e.id, { ativo: true });
                                carregar();
                              }}
                            >
                              Reativar
                            </AsyncButton>
                          )}
                        </>
                      )}
                    </AcoesLinha>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <FormEndpoint
        aberto={formAberto}
        endpoint={editando}
        eventos={eventos}
        onFechar={() => { setFormAberto(false); setEditando(null); }}
        onSalvo={aoSalvar}
      />
      <PainelSecret
        secret={secretExibido}
        contexto={secretContexto}
        onFechar={() => setSecretExibido(null)}
      />
      <PainelEntregas
        endpoint={endpointEntregas}
        onFechar={() => setEndpointEntregas(null)}
      />
    </PageContainer>
  );
}
